import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import moment from 'moment'
import { useMemo } from 'react'

import { useVariables } from 'pared/Routes/renderer/variables'

import { useDateFilter } from '../../../../dateFilter'
import { useGroupFilter } from '../../../../groupFilter'
import { IApiDataType } from '../../../types'

type IMetricDataType = Record<
  string,
  {
    name?: string
    unit: string
    value: number | null
  }
>

const query = gql`
  query listStorePerforamceTable(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        startDate
        endDate
        businessQuarter
        businessMonth
        businessWeek
        metricData
      }
    }

    listLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        metricSummaryData
      }
    }
  }
`

const formatDateString = (dateStr: string) => moment(dateStr).format('M/D/YY')

const format = (data: IMetricDataType) =>
  Object.entries(data).reduce((result, [key, value]) => {
    switch (value.unit) {
      case 'PERCENTAGE':
      case 'DOLLAR':
        return {
          ...result,
          [_.camelCase(key)]: value.value === null ? null : value.value * 100,
        }
      default:
        return {
          ...result,
          [_.camelCase(key)]: value.value,
        }
    }
  }, {})

export const fwWingstopStorePerformanceConfigs = {
  date: 'string',
  netSales: 'price',
  laborActualCost: 'price',
  laborActualPercent: 'percent',
  actualFoodCost: 'price',
  actualFoodCostPercentage: 'percent',
} as const

const useStorePerformance = () => {
  const { variables } = useVariables()
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()

  const dateType = variables.date?.value.type
  const iGroupBy = ((t) => {
    switch (t) {
      case 'year':
        return 'business_quarter'

      case 'quarter':
        return 'business_month'

      default:
        return 'business_week'
    }
  })(dateType)

  const { data, loading } = useQuery(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iGroupBy,
      iFilter: {
        location_ids: groupFilter?.ids,
        metrics: [
          'net_sales',
          'sales_forecast',
          'labor_actual_cost',
          'labor_actual_percent',
          'labor_scheduled_cost',
          'labor_scheduled_percent',
          'actual_food_cost',
          'actual_food_cost_percentage',
          'theory_food_cost',
          'theory_food_cost_percentage',
        ],
      },
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo((): IApiDataType => {
      const trendData = data?.trendLocationMetricValues.nodes as
        | {
            startDate: string
            endDate: string
            businessQuarter: number
            businessMonth: number
            businessWeek: number
            metricData: {
              [key: string]: {
                name: string
                unit: string
                value: number
              }
            }
          }[]
        | undefined

      const locationData = data?.listLocationMetricValues.nodes as
        | {
            metricSummaryData: {
              [key: string]: {
                name: string
                unit: string
                value: number
              }
            }
          }[]
        | undefined

      if (!trendData || !locationData) return null

      const resultData = trendData.map((d) => {
        const startStr = formatDateString(d.startDate)
        const endDateStr = formatDateString(d.endDate)

        const date = `
            <div style="font-family: Lexend-SemiBold; font-weight: 700; margin-top: 5px;">
              ${(() => {
                switch (dateType) {
                  case 'year':
                    return `Quarter ${d.businessQuarter}`
                  case 'quarter':
                    return `Period ${d.businessMonth}`
                  default:
                    return `Week ${d.businessWeek}`
                }
              })()}
            </div>
            <div style="margin-bottom: 5px;">
              ${startStr} - ${endDateStr}
            </div>
          `

        return {
          ...format(d.metricData),
          date,
          id: date as string,
          parentId: 'root',
        }
      })

      const summaryData =
        locationData.find((s) => !!s.metricSummaryData)?.metricSummaryData || {}

      const netSalesUnit =
        summaryData['net_sales']?.unit ?? summaryData['sales_forecast']?.unit
      const laborActualCostUnit =
        summaryData['labor_actual_cost']?.unit ??
        summaryData['labor_scheduled_cost']?.unit
      const laborActualPercentUnit =
        summaryData['labor_actual_percent']?.unit ??
        summaryData['labor_scheduled_percent']?.unit
      const actualFoodCostUnit =
        summaryData['actual_food_cost']?.unit ??
        summaryData['theory_food_cost']?.unit
      const actualFoodCostPercentageUnit =
        summaryData['actual_food_cost_percentage']?.unit ??
        summaryData['theory_food_cost_percentage']?.unit

      const ptdNetSales = summaryData['net_sales']?.value ?? null
      const ptdSalesForecast = summaryData['sales_forecast']?.value ?? null
      const ptdLaborActualCost = summaryData['labor_actual_cost']?.value ?? null
      const ptdLaborActualPercent =
        summaryData['labor_actual_percent']?.value ?? null
      const ptdLaborScheduledCost =
        summaryData['labor_scheduled_cost']?.value ?? null
      const ptdLaborScheduledPercent =
        summaryData['labor_scheduled_percent']?.value ?? null
      const ptdActualFoodCost = summaryData['actual_food_cost']?.value ?? null
      const ptdActualFoodCostPercentage =
        summaryData['actual_food_cost_percentage']?.value ?? null
      const ptdTheoryFoodCost = summaryData['theory_food_cost']?.value ?? null
      const ptdTheoryFoodCostPercentage =
        summaryData['theory_food_cost_percentage']?.value ?? null

      const ptdBudgetData = {
        net_sales: { unit: netSalesUnit, value: ptdSalesForecast },
        labor_actual_cost: {
          unit: laborActualCostUnit,
          value: ptdLaborScheduledCost,
        },
        labor_actual_percent: {
          unit: laborActualPercentUnit,
          value: ptdLaborScheduledPercent,
        },
        actual_food_cost: {
          unit: actualFoodCostUnit,
          value: ptdTheoryFoodCost,
        },
        actual_food_cost_percentage: {
          unit: actualFoodCostPercentageUnit,
          value: ptdTheoryFoodCostPercentage,
        },
      }

      const ptdDifferenceData = {
        net_sales: {
          unit: netSalesUnit,
          value:
            ptdNetSales === null || ptdSalesForecast === null
              ? null
              : ptdNetSales - ptdSalesForecast,
        },
        labor_actual_cost: {
          unit: laborActualCostUnit,
          value:
            ptdLaborActualCost === null || ptdLaborScheduledCost === null
              ? null
              : ptdLaborActualCost - ptdLaborScheduledCost,
        },
        labor_actual_percent: {
          unit: laborActualPercentUnit,
          value:
            ptdLaborActualPercent === null || ptdLaborScheduledPercent === null
              ? null
              : ptdLaborActualPercent - ptdLaborScheduledPercent,
        },
        actual_food_cost: {
          unit: actualFoodCostUnit,
          value:
            ptdActualFoodCost === null || ptdTheoryFoodCost === null
              ? null
              : ptdActualFoodCost - ptdTheoryFoodCost,
        },
        actual_food_cost_percentage: {
          unit: actualFoodCostPercentageUnit,
          value:
            ptdActualFoodCostPercentage === null ||
            ptdTheoryFoodCostPercentage === null
              ? null
              : ptdActualFoodCostPercentage - ptdTheoryFoodCostPercentage,
        },
      }

      const resultSummary = [
        {
          ...format(summaryData),
          date: 'PTD Actual',
          id: 'ptdActual',
          parentId: 'root',
        },
        {
          ...format(ptdBudgetData),
          date: 'PTD Forecast/Theoretical/Scheduled',
          id: 'ptdBudget',
          parentId: 'root',
        },
        {
          ...format(ptdDifferenceData),
          date: 'Difference',
          id: 'ptdDifference',
          parentId: 'root',
        },
      ]

      return [...resultSummary, ...resultData]
    }, [data]),
    loading,
  }
}

export default useStorePerformance
